import React from "react";
import {
	Box,
	Typography,
	Button,
	CircularProgress,
	Paper,
	useTheme,
	useMediaQuery
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { zoullooColors } from "../../theme";
import { TablePagination, SortOption, SortConfig } from "../common/pagination";
import { Project } from "../../models/Project";
import { Company } from "../../models/Company";
import { ProjectsSortField } from "../../models/enums/ProjectsSortField";
import ProjectCard from "./ProjectCard";
import ProjectFilters from "./ProjectFilters";
import ProjectTable from "./ProjectTable";

interface ProjectListTableProps {
	projects: Project[];
	totalCount: number;
	totalPages: number;
	isLoading: boolean;
	error: Error | null;
	canCreateProject: boolean;
	showFeePercentage: boolean;
	showCompanyFilter: boolean;
	title: string;
	createButtonText: string;
	onCreateProject: () => void;
	onViewProject: (id: number) => void;
	onEditProject: (id: number) => void;
	canEditProject: (project: Project) => boolean;
	page: number;
	pageSize: number;
	statusFilter: string;
	nameFilter: string;
	projectNumberFilter: string;
	companyFilter?: Company | null;
	sortField?: string;
	sortDirection?: string;
	onPageChange: (page: number) => void;
	onPageSizeChange: (pageSize: number) => void;
	onStatusFilterChange: (status: string) => void;
	onNameFilterChange: (name: string) => void;
	onProjectNumberFilterChange: (projectNumber: string) => void;
	onCompanyFilterChange: (company: Company | null) => void;
	onSortChange: (field: string, direction: string) => void;
	onSearch: () => void;
	onReset: () => void;
}

const ProjectListTable: React.FC<ProjectListTableProps> = ({
	projects,
	totalCount,
	totalPages,
	isLoading,
	error,
	canCreateProject,
	showFeePercentage,
	showCompanyFilter,
	title,
	createButtonText,
	onCreateProject,
	onViewProject,
	onEditProject,
	canEditProject,
	page,
	pageSize,
	statusFilter,
	nameFilter,
	projectNumberFilter,
	companyFilter,
	sortField,
	sortDirection,
	onPageChange,
	onPageSizeChange,
	onStatusFilterChange,
	onNameFilterChange,
	onProjectNumberFilterChange,
	onCompanyFilterChange,
	onSortChange,
	onSearch,
	onReset
}) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));

	// Create sort options for projects
	const sortOptions: SortOption[] = [
		{ label: t('project.sort.name'), value: ProjectsSortField.Name },
		{ label: t('project.sort.projectNumber'), value: ProjectsSortField.ProjectNumber },
		{ label: t('project.sort.company'), value: ProjectsSortField.Company },
		{ label: t('project.sort.status'), value: ProjectsSortField.Status },
		{ label: t('project.sort.desiredStartDate'), value: ProjectsSortField.DesiredStartDate },
		{ label: t('project.sort.createdAt'), value: ProjectsSortField.CreatedAt },
		{ label: t('project.sort.budget'), value: ProjectsSortField.Budget }
	];

	// Create current sort configuration
	const currentSort: SortConfig = {
		sortField,
		sortDirection: sortDirection === 'asc' ? 'Asc' : 'Desc'
	};

	// Handle sort change from pagination component
	const handleSortChange = (sort: SortConfig) => {
		onSortChange(sort.sortField || '', sort.sortDirection === 'Asc' ? 'asc' : 'desc');
	};

	if (isLoading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" height="400px">
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return (
			<Box p={3}>
				<Typography color="error">
					{t("common.errorLoading")}: {error.message}
				</Typography>
			</Box>
		);
	}

	return (
		<Box px={isMobile ? 2 : 3} py={4} sx={{ width: '100%', maxWidth: '100vw', overflow: 'hidden' }}>
			{/* Header */}
			<Box
				display="flex"
				flexDirection={isMobile ? "column" : "row"}
				justifyContent="space-between"
				alignItems={isMobile ? "stretch" : "center"}
				mb={3}
				gap={isMobile ? 2 : 0}
			>
				<Box>
					<Typography variant={isMobile ? "h4" : "h2"}>{title}</Typography>
					<Typography variant="body1" color="text.secondary" mt={1}>
						{t("project.showing")} {totalCount} {t("project.projects")}
					</Typography>
				</Box>
				{canCreateProject && (
					<Button
						variant="contained"
						startIcon={<AddIcon />}
						onClick={onCreateProject}
						fullWidth={isMobile}
						sx={{
							backgroundColor: zoullooColors.secondary.main,
							color: zoullooColors.secondary.contrastText,
							"&:hover": {
								backgroundColor: zoullooColors.secondary.dark
							}
						}}
					>
						{createButtonText}
					</Button>
				)}
			</Box>

			<ProjectFilters
				isMobile={isMobile}
				nameFilter={nameFilter}
				projectNumberFilter={projectNumberFilter}
				statusFilter={statusFilter}
				companyFilter={companyFilter}
				showCompanyFilter={showCompanyFilter}
				onNameFilterChange={onNameFilterChange}
				onProjectNumberFilterChange={onProjectNumberFilterChange}
				onStatusFilterChange={onStatusFilterChange}
				onCompanyFilterChange={onCompanyFilterChange}
				onSearch={onSearch}
				onReset={onReset}
			/>

			{isMobile ? (
				<Box>
					{projects.length === 0 ? (
						<Paper elevation={1} sx={{ p: 4, textAlign: "center" }}>
							<Typography variant="h6" gutterBottom>
								{t("project.noProjectsFound")}
							</Typography>
						</Paper>
					) : (
						projects.map(project => (
							<ProjectCard
								key={project.id}
								project={project}
								showFeePercentage={showFeePercentage}
								onViewProject={onViewProject}
								onEditProject={onEditProject}
								canEditProject={canEditProject}
							/>
						))
					)}
				</Box>
			) : (
				<Box sx={{ width: '100%', overflowX: 'auto' }}>
					<ProjectTable
						projects={projects}
						sortField={sortField}
						sortDirection={sortDirection}
						onViewProject={onViewProject}
						onEditProject={onEditProject}
						canEditProject={canEditProject}
						onSortChange={onSortChange}
					/>
				</Box>
			)}

			{/* Unified Pagination with Sort */}
			{totalPages > 0 && (
				<TablePagination
					currentPage={page}
					pageSize={pageSize}
					totalCount={totalCount}
					totalPages={totalPages}
					onPageChange={onPageChange}
					onPageSizeChange={onPageSizeChange}
					pageSizeOptions={[10, 20, 50]}
					sortOptions={sortOptions}
					currentSort={currentSort}
					onSortChange={handleSortChange}
					disabled={false}
					sticky={false}
					mx={0}
				/>
			)}
		</Box>
	);
};

export default ProjectListTable;
